import React from 'react'
import {useSelector} from 'react-redux'
import {PriceTrendsTrackingContext} from 'components/PriceTrend/types'
import useShowOverlay from 'hooks/useShowOverlay/useShowOverlay'
import {
  getParametrizedHotelContext,
  getSearchContext
} from 'middleware/analytics/selectors'
import {
  getCurrencyCode,
  getDeviceType,
  getLocaleCode,
  getLocalTaxesIncluded,
  getTaxesIncluded,
  getUserCountryCode
} from 'modules/meta/selectors'
import {OverlayType} from 'modules/overlay/types'
import {
  getNumberOfGuests,
  getNumberOfRooms,
  getOfferEntity,
  getShowTotalPrices
} from 'modules/sapiSearch/selectors'
import {getSearchBoxValues} from 'modules/searchBox/selectors'
import {
  getCheapestOfferTotalRate,
  getCheapestTopOfferByNightlyRate
} from 'utils/offers'

import {useBrandCapabilities} from '@vio/core/src/_web/brand/hooks/useBrandCapabilities'
import {
  getWhiteLabelUserId,
  selectIsAuthenticated
} from '@vio/core/src/auth/modules/selectors'
import {PriceWatchCTA} from '@vio/shared/src/priceWatch/components/PriceWatchCTA'
import {useIsSaveFraming} from '@vio/shared/src/priceWatch/hooks/useIsSaveFraming'
import {
  PriceWatchCTAParams,
  PriceWatchPayload
} from '@vio/shared/src/priceWatch/types'

import {ALERTS_ROUTE} from '../../constants'

import type {ContentHotel, Hotel} from '@findhotel/sapi'

interface Props {
  hotel?: Hotel | ContentHotel | undefined
  ctaComponent: PriceWatchCTAParams['ctaComponent']
  shouldPulse?: boolean
  searchIsCompleted: boolean
  componentName: string
  className?: string
  priceTrendsContext?: PriceTrendsTrackingContext
  /** Holds the toggle's label on alert wording. The icon and the toast still
   * follow the experiment. */
  hasAlertText?: boolean
}

export const PriceWatchCTAWrapper = ({
  className,
  hotel,
  searchIsCompleted,
  ctaComponent,
  shouldPulse = true,
  componentName,
  priceTrendsContext,
  hasAlertText = false
}: Props) => {
  const currencyCode = useSelector(getCurrencyCode)
  const locale = useSelector(getLocaleCode)
  const localTaxesIncluded = useSelector(getLocalTaxesIncluded)
  const taxesIncluded = useSelector(getTaxesIncluded)
  const isAuthenticated = useSelector(selectIsAuthenticated)
  const whiteLabelUserId = useSelector(getWhiteLabelUserId)
  const hotelContext = getParametrizedHotelContext(hotel?.objectID)
  const {checkIn, checkOut, rooms} = useSelector(getSearchBoxValues)
  const userCountry = useSelector(getUserCountryCode)
  const deviceType = useSelector(getDeviceType)
  const {retention} = useBrandCapabilities()
  const roomsNumber = useSelector(state =>
    getNumberOfRooms(state, hotel?.objectID)
  )
  const guests = useSelector(getNumberOfGuests)
  const offerEntity = useSelector(state =>
    getOfferEntity(state, hotel?.objectID)
  )

  const cheapestOffer = getCheapestTopOfferByNightlyRate(offerEntity?.offers)
  const cheapestTotalRate = getCheapestOfferTotalRate(offerEntity?.offers)
  const shouldShowTotal = useSelector(getShowTotalPrices)
  const showOverlay = useShowOverlay()

  const shouldHideIcon = componentName === 'PriceTrend'

  const isPriceWatchEnabled = retention.hasPriceWatch

  const rate = {
    taxesIncluded,
    localTaxesIncluded,
    base: cheapestOffer?.rate.base,
    hotelFees: cheapestOffer?.rate.hotelFees,
    taxes: cheapestOffer?.rate.taxes
  } as PriceWatchPayload['rate']

  const shouldShowCta =
    hotel && cheapestOffer && searchIsCompleted && isPriceWatchEnabled

  const saveFramingIsOn = useIsSaveFraming()

  const setWebBottomSheetOpen = () => {
    showOverlay({
      type: OverlayType.PriceWatchOverlay,
      params: {
        hotel,
        rate: {
          baseRate: rate.base,
          localTaxes: rate.hotelFees,
          taxes: rate.taxes,
          nightlyRate: cheapestOffer?.nightlyRate,
          totalRate: cheapestTotalRate,
          localTaxesIncluded,
          taxesIncluded,
          shouldShowTotal,
          currency: cheapestOffer?.currency
        },
        flow: componentName,
        priceTrendsContext
      }
    })
  }

  if (!shouldShowCta) return null

  return (
    <PriceWatchCTA
      className={className}
      checkIn={checkIn}
      checkOut={checkOut}
      hotel={hotel}
      rooms={rooms}
      roomsNumber={roomsNumber}
      currency={currencyCode}
      locale={locale}
      rate={rate}
      getSearchContext={getSearchContext}
      hotelContext={hotelContext}
      isAuthenticated={Boolean(isAuthenticated || whiteLabelUserId)}
      roomName={cheapestOffer?.roomName}
      ctaComponent={ctaComponent}
      componentName={componentName}
      guests={guests}
      setWebBottomSheetOpen={setWebBottomSheetOpen}
      shouldPulse={shouldPulse}
      hideIcon={shouldHideIcon}
      userCountry={userCountry}
      device={deviceType}
      alertsManagementUrl={ALERTS_ROUTE}
      trackingContext={priceTrendsContext}
      saveFramingIsOn={saveFramingIsOn}
      hasAlertText={hasAlertText}
    />
  )
}
